import React, { useMemo, useState } from 'react';
import { submitFeedback, submitNodeFeedbackBulk } from 'features/evals3/api/evals3Api';
import StepAnswer, { specOf, saidText, complete } from './StepAnswer';

/**
 * A verdict marked wrong is reviewed step by step. Every step on the path
 * gets an answer (Right, or what it should have said), then the verdict
 * (Pass or Fail) and why. Save needs all of it.
 *
 * What the answers mean:
 *   a step corrected       fix_kind 'step'  the step label trains the model
 *                                           and the fix is in that step
 *   every step right       fix_kind 'eval'  the eval is missing a check; the
 *                                           design question goes to Lucid
 */
export default function VerdictReview({ teamId, run, steps, nodeFor, labels = {}, feedback = null, onSaved, onCancel }) {
  const list = useMemo(() => (steps || []).filter((s) => s && s.node_id && !s.error), [steps]);
  const [answers, setAnswers] = useState(() => Object.fromEntries(list.map((s) => {
    const l = labels[s.node_id];
    return [s.node_id, l ? { agree: l.agree === true, should_have_been: l.should_have_been || {} } : null];
  })));
  const [correctedPassed, setCorrectedPassed] = useState(feedback?.corrected_passed ?? (typeof run?.passed === 'boolean' ? !run.passed : null));
  const [reason, setReason] = useState(feedback?.reason_text || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const done = list.every((s) => complete(specOf(nodeFor(s.node_id)), answers[s.node_id]));
  const corrected = list.filter((s) => answers[s.node_id] && !answers[s.node_id].agree);
  const fixKind = !done ? null : corrected.length ? 'step' : 'eval';
  const ready = done && typeof correctedPassed === 'boolean' && reason.trim().length > 0;
  const stop = (e) => { e.stopPropagation(); };

  const save = async (e) => {
    stop(e);
    if (!ready || busy) return;
    setBusy(true); setError(null);
    try {
      const items = list.map((s) => {
        const a = answers[s.node_id];
        const spec = specOf(nodeFor(s.node_id));
        return a.agree
          ? { eval_run_id: run.id, node_id: s.node_id, agree: true }
          : { eval_run_id: run.id, node_id: s.node_id, should_have_been: spec ? Object.fromEntries(Object.keys(spec).map((k) => [k, a.should_have_been[k]])) : { text: a.should_have_been.text || '' } };
      });
      const out = items.length ? await submitNodeFeedbackBulk(teamId, items) : { labelled: [], skipped: [] };
      if (out?.skipped?.length) throw new Error(out.skipped.map((x) => x.message).join('; '));
      const saved = await submitFeedback(teamId, { eval_run_id: run.id, thumbs: 'down', corrected_passed: correctedPassed, reason_text: reason.trim(), steps_reviewed: true, fix_kind: fixKind });
      onSaved?.({ feedback: saved, labels: out?.labelled || [], fixKind, steps: list.map((s) => ({ step: s, node: nodeFor(s.node_id), answer: answers[s.node_id], label: (out?.labelled || []).find((l) => l.node_id === s.node_id) || null })) });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not save');
    } finally { setBusy(false); }
  };

  const btn = 'rounded border px-2 py-1 text-[11px]';
  const on = 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]';
  const off = 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover';

  return (
    <div className="mb-2 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-3 text-[12px]" onClick={stop} role="group" aria-label="Review the steps" data-testid="verdict-review">
      <div className="mb-2 font-medium text-[color:var(--text-primary)]">Where did it go wrong?</div>
      {list.length === 0 && <div className="mb-2 text-[color:var(--text-muted)]">No steps recorded.</div>}
      <ol className="space-y-2">
        {list.map((s) => {
          const node = nodeFor(s.node_id);
          const said = s.output?.value ?? s.value ?? null;
          return (
            <li key={s.node_id} className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t border-[color:var(--border-subtle)] pt-2 first:border-t-0 first:pt-0">
              <div className="min-w-0 flex-1 basis-48">
                <div className="truncate font-medium text-[color:var(--text-primary)]">{node?.name || s.node_id}</div>
                <div className="truncate text-[11px] text-[color:var(--text-muted)]" title={saidText(specOf(node), said)}>Said {saidText(specOf(node), said)}</div>
              </div>
              <div className="min-w-0 flex-1 basis-64">
                <StepAnswer compact node={node} said={said} value={answers[s.node_id]} onChange={(v) => setAnswers((m) => ({ ...m, [s.node_id]: v }))} />
              </div>
            </li>
          );
        })}
      </ol>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[color:var(--border-subtle)] pt-2">
        <span className="text-[color:var(--text-secondary)]">Verdict should have been</span>
        {[['Pass', true], ['Fail', false]].map(([label, v]) => (
          <button key={label} type="button" onClick={() => setCorrectedPassed(v)} aria-pressed={correctedPassed === v} className={`${btn} ${correctedPassed === v ? on : off}`}>{label}</button>
        ))}
        {fixKind === 'eval' && <span className="ml-auto text-[11px] text-[color:var(--text-muted)]" data-testid="fix-kind">Every step was right: the eval is missing a check.</span>}
        {fixKind === 'step' && <span className="ml-auto text-[11px] text-[color:var(--text-muted)]" data-testid="fix-kind">{corrected.length} step{corrected.length === 1 ? '' : 's'} corrected.</span>}
      </div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={4000} aria-label="Why"
        placeholder="Why?" className="mt-2 w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5 text-[11px] text-[color:var(--text-primary)] outline-none" />
      {error && <div className="mt-1 text-[11px] text-[color:var(--feedback-error)]" role="alert">{error}</div>}
      <div className="mt-2 flex items-center gap-1">
        <button type="button" disabled={!ready || busy} onClick={save} title={ready ? undefined : 'Answer every step, pick Pass or Fail, and say why'}
          className="rounded bg-brand px-2.5 py-1 text-[11px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} className="rounded px-2 py-1 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
      </div>
    </div>
  );
}
